import React from 'react';
import { BasicForm, notification, TextAreaFieldItem, Typography } from 'ui-lib';
import { CheckboxGroupItem, TextFieldItem, IconTextButton, useForm, } from 'ui-lib';
import fileTransferCss from './new-file-transfer.css';
import { useDeviceGroupsStore, useFileTransfersStore, useStores } from 'store';
import { getErrorMessage, isNullOrEmpty } from 'common-utils';
import { confirmAction } from 'pages/private/admin/utils';
import { FileTransferDto } from 'dto/file-transfer.dto';
import { PERMISSIONS_OPTIONS } from './transfer-files-tab';

interface IEditFileProps {
    selectedClientAccountId: number;
    selectedDeviceGroup: string;
    selectedFileTransfer: FileTransferDto;
    onCancel?: () => void;
}

export const EditTransferFile = (props: IEditFileProps) => {
    const stores = useStores();
    const deviceGroupsStore = useDeviceGroupsStore();
    const fileTransfersStore = useFileTransfersStore();
    const [form] = useForm();
    const [permissionsCheckedList, setPermissionsCheckedList] = React.useState([4]);
    const [destination, setFolderPath] = React.useState(props.selectedFileTransfer?.destinationPath || '');
    const [description, setDescription] = React.useState(props.selectedFileTransfer?.description || '');
    const [executeCommand, setExecuteCommand] = React.useState(props.selectedFileTransfer?.executeCommand || '');

    React.useEffect(() => {
        deviceGroupsStore.loadDeviceGroupDetails();
    }, []);

    React.useEffect(() => {
        if (props.selectedFileTransfer?.permissions) {
            const permissions = props.selectedFileTransfer.permissions.split('').map(Number);
            const permission = permissions.length > 0 ? permissions[0] : 0;
            const permissionCheckValue = [permission & 1, permission & 2, permission & 4];
            setPermissionsCheckedList(permissionCheckValue);
            form.resetFields(['permissions']);
            form.setFieldsValue({ 'permissions': permissionCheckValue });
        }
    }, [props.selectedFileTransfer]);

    const deviceGroupLabel = React.useMemo(() => {
        return deviceGroupsStore.getDeviceGroupByName(props.selectedDeviceGroup)?.deviceGroupLabel
    }, [props.selectedDeviceGroup]);

    const updateFile = async (values: any) => {
        try {
            if (isNullOrEmpty(props.selectedFileTransfer?.uuid)) {
                throw new Error('File transfer UUID is empty.');
            }
            const permissionsCode = permissionsCheckedList.reduce((p: number, c) => p + Number(c), 0);
            const permissionsCodeLinux = `${permissionsCode}${permissionsCode}${permissionsCode}`;

            const result = await fileTransfersStore.updateFileTransfer(
                props.selectedClientAccountId,
                props.selectedFileTransfer.uuid,
                {
                    destinationPath: destination,
                    executeCommand: isNullOrEmpty(values.shellCommandField) ? '' : values.shellCommandField,
                    permissions: permissionsCodeLinux,
                    description: description,
                },
            );

            if (result) {
                notification.success({
                    message: 'File Transfer Successfully Updated',
                    description: `File transfer ${props.selectedFileTransfer.fileName} has been updated.`,
                });
                props.onCancel();
            }
        } catch (err) {
            notification.error({
                message: getErrorMessage(err),
                placement: 'topRight',
            });
        }
    };

    const handleSubmit = (values: any) => {
        confirmAction(values, 'Update File', 'Do you want to update this file?', updateFile);
    };

    return (
        <React.Fragment>
            <Typography.Title level={2}>Edit Transfer File</Typography.Title>
            <BasicForm
                title='Edit File Transfer'
                form={form}
                onSubmit={handleSubmit}
                className={fileTransferCss['transfer-form']}
                items={[
                    <TextFieldItem
                        key='deviceGroup'
                        code='deviceGroup'
                        label='Device Group'
                        colon={true}
                        labelAlign='right'
                        initialValue={deviceGroupLabel}
                        disabled={true}
                    />,
                    <TextFieldItem
                        key='fileName'
                        code='fileName'
                        label='File Name'
                        colon={true}
                        labelAlign='right'
                        initialValue={props.selectedFileTransfer.fileName}
                        disabled={true}
                    />,
                    <TextFieldItem
                        key='fileVersion'
                        code='fileVersion'
                        label='File Version'
                        colon={true}
                        labelAlign='right'
                        initialValue={props.selectedFileTransfer.version}
                        disabled={true}
                    />,
                    <TextFieldItem
                        initialValue={destination}
                        key='folderPath'
                        code='folderPath'
                        label='Folder path'
                        colon={true}
                        isRequired={true}
                        labelAlign='right'
                        onChange={(value) => setFolderPath(value.target.value)}
                    />,
                    <TextAreaFieldItem
                        initialValue={executeCommand}
                        labelColSpan={5}
                        code='shellCommandField'
                        label='Execution script'
                        colon={true}
                        labelAlign='right'
                        isRequired={false}
                        textAreaProps={{
                            placeholder: 'Here you can specify a script or command which is executed when file is downloaded. Ex.\n#!/bin/bash\nsrh -fw scr-config',
                            autoSize: { minRows: 10, maxRows: 200 },
                        }}
                    />,
                    <TextFieldItem
                        initialValue={description}
                        key='description'
                        code='description'
                        label='Description'
                        colon={true}
                        isRequired={false}
                        labelAlign='right'
                        onChange={(value) => setDescription(value.target.value)}
                    />,
                    <CheckboxGroupItem
                        labelAlign='right'
                        key='permissions'
                        code='permissions'
                        defaultValue={permissionsCheckedList}
                        label='Permissions'
                        colon={true}
                        options={PERMISSIONS_OPTIONS}
                        value={permissionsCheckedList}
                        onChange={(value) => setPermissionsCheckedList(value)}
                    />,
                    <div className={fileTransferCss['row']}>
                        <IconTextButton
                            key='back-button'
                            className={fileTransferCss['footer-left-button']}
                            htmlType='button'
                            label='Back'
                            onClick={props.onCancel}
                        />
                        <IconTextButton
                            key='update-submit-button'
                            className={fileTransferCss['footer-right-button']}
                            htmlType='submit'
                            label='Update'
                            type='primary'
                        />
                    </div>,
                ]}
            />
        </React.Fragment>
    );
};
